/**
 * kennisKluisService.js — één toegangspunt tot de Kennis-Kluis.
 *
 * De Kennis-Kluis draait in het netwerk van de klant en vereist sinds de
 * beveiligingsupdate een API-sleutel. Alle aanroepen lopen via dit bestand,
 * zodat de sleutel op één plek staat en niet in elke component herhaald wordt.
 *
 * LET OP over de sleutel:
 * Een VITE_-variabele wordt in de bundel gebakken en is dus leesbaar voor
 * iedereen die de app kan openen. Daarom hoort hier uitsluitend de LEESsleutel
 * te staan, nooit de schrijfsleutel: uploaden en verwijderen doe je vanaf de
 * server of met curl. De leessleutel geeft toegang tot de kennisbank van de
 * klant, dus behandel hem als vertrouwelijk en per klant uniek.
 */

const BASE_URL = (import.meta.env.VITE_KENNIS_KLUIS_API || 'http://localhost:8000').replace(/\/$/, '');
const API_KEY = import.meta.env.VITE_KENNIS_KLUIS_KEY || '';

/**
 * Optionele schrijfsleutel voor uploaden en verwijderen.
 *
 * Bewuste afweging: ook deze belandt in de bundel en is dus leesbaar voor
 * iedere gebruiker van de app. Wie hem uitleest kan documenten uploaden en
 * de hele kennisbank verwijderen. Zet hem daarom alleen als u die functie
 * echt in de app wilt, en bij voorkeur in een aparte beheerdersbuild.
 * Laat u hem leeg, dan verbergt de app de uploadfunctie en doet u uploads
 * vanaf de server (zie INSTALLATIE.md).
 */
const WRITE_KEY = import.meta.env.VITE_KENNIS_KLUIS_WRITE_KEY || '';

/** Is de koppeling geconfigureerd? Zonder sleutel weigert de server alles. */
export const isConfigured = () => Boolean(BASE_URL && API_KEY);

/** Mag deze build uploaden? Zo nee, hoort de uploadknop verborgen te zijn. */
export const canUpload = () => Boolean(BASE_URL && WRITE_KEY);

export class KennisKluisError extends Error {
    constructor(message, { status = null, unauthorized = false, offline = false } = {}) {
        super(message);
        this.name = 'KennisKluisError';
        this.status = status;
        this.unauthorized = unauthorized;
        this.offline = offline;
    }
}

function headers(extra = {}) {
    const h = { ...extra };
    if (API_KEY) h['X-API-Key'] = API_KEY;
    return h;
}

async function request(path, { method = 'GET', body = null, signal = null } = {}) {
    let res;
    try {
        res = await fetch(`${BASE_URL}${path}`, {
            method,
            signal,
            headers: headers(body ? { 'Content-Type': 'application/json' } : {}),
            body: body ? JSON.stringify(body) : undefined,
        });
    } catch (err) {
        // Netwerkfout: de kluis staat uit, of de monteur heeft geen verbinding.
        throw new KennisKluisError('Kennis-Kluis niet bereikbaar', { offline: true });
    }

    if (res.status === 401) {
        throw new KennisKluisError(
            'Geen toegang tot de Kennis-Kluis. Controleer de API-sleutel.',
            { status: 401, unauthorized: true }
        );
    }

    let data = null;
    try {
        data = await res.json();
    } catch {
        data = null;
    }

    if (!res.ok) {
        throw new KennisKluisError(data?.error || `Fout ${res.status} van de Kennis-Kluis`, { status: res.status });
    }
    return data;
}

/** Is de kluis bereikbaar? Werkt zonder sleutel, want /health is publiek. */
export async function health() {
    return request('/health');
}

/**
 * Vraag over een specifiek onderdeel.
 * stream staat expliciet op false: de component verwerkt JSON, geen SSE.
 */
export async function askAsset({ vraag, assetId, user, signal }) {
    return request('/ask/asset', {
        method: 'POST',
        signal,
        body: { vraag, asset_id: assetId, user: user || 'anonymous', stream: false },
    });
}

/** Vraag over de hele kennisbank (demo-modus). */
export async function ask({ vraag, user, signal }) {
    return request('/ask', {
        method: 'POST',
        signal,
        body: { vraag, user: user || 'anonymous', stream: false },
    });
}

/** Wat weet de kluis over dit onderdeel? */
export async function assetContext(assetId, { signal } = {}) {
    return request(`/asset/${encodeURIComponent(assetId)}/context`, { signal });
}

/** Documenten in de kennisbank. */
export async function listDocuments() {
    return request('/docs');
}

/**
 * Beoordeling op een antwoord. Dit sluit de lus: zonder deze stap
 * leert het systeem niets van wat de monteur ervan vond.
 */
export async function sendFeedback({ feedbackId, rating, correctie = '' }) {
    return request('/feedback', {
        method: 'POST',
        body: { feedback_id: feedbackId, rating, correctie },
    });
}

/**
 * Document toevoegen aan de kennisbank. Vereist de schrijfsleutel.
 * Gooit een duidelijke fout als die niet is ingesteld, zodat het
 * niet als een vage 401 bij de gebruiker terechtkomt.
 */
export async function uploadDocument(file) {
    if (!canUpload()) {
        throw new KennisKluisError(
            'Uploaden is in deze installatie niet ingeschakeld. Voeg documenten toe via de server.',
            { status: 403 }
        );
    }
    const formData = new FormData();
    formData.append('file', file);

    let res;
    try {
        res = await fetch(`${BASE_URL}/upload`, {
            method: 'POST',
            headers: { 'X-API-Key': WRITE_KEY },
            body: formData,
        });
    } catch {
        throw new KennisKluisError('Kennis-Kluis niet bereikbaar', { offline: true });
    }

    if (res.status === 401) {
        throw new KennisKluisError('De schrijfsleutel wordt niet geaccepteerd.', {
            status: 401, unauthorized: true,
        });
    }
    const data = await res.json().catch(() => null);
    if (!res.ok) {
        throw new KennisKluisError(data?.error || `Upload mislukt (${res.status})`, { status: res.status });
    }
    return data;
}

export default {
    isConfigured,
    canUpload,
    uploadDocument,
    health,
    ask,
    askAsset,
    assetContext,
    listDocuments,
    sendFeedback,
    KennisKluisError,
};
